import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";

const DISMISS_KEY = "pmposhan_pwa_prompt_dismissed_at";
const DISMISS_TTL_DAYS = 14;
const CONFETTI_KEY = "pmposhan_pwa_installed_celebrated";

function celebrateInstall() {
  try {
    if (localStorage.getItem(CONFETTI_KEY)) return;
    localStorage.setItem(CONFETTI_KEY, String(Date.now()));
  } catch (_) {}
  // Two-burst confetti from bottom corners so it feels rewarding on mobile.
  const shoot = (x) => confetti({
    particleCount: 80,
    spread: 70,
    startVelocity: 45,
    origin: { x, y: 1 },
    colors: ["#047857", "#10b981", "#fbbf24", "#f97316", "#ffffff"],
    zIndex: 9999,
  });
  shoot(0.15);
  setTimeout(() => shoot(0.85), 180);
  setTimeout(() => confetti({
    particleCount: 40, spread: 120, startVelocity: 25,
    origin: { x: 0.5, y: 0.6 }, ticks: 220, zIndex: 9999,
    colors: ["#047857", "#10b981", "#fbbf24"],
  }), 360);
  toast.success("🎉 Added to Home Screen — enjoy one-tap access!", { duration: 4500 });
}

export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Skip if already installed (standalone) or previously dismissed within TTL
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    try {
      const dismissedAt = localStorage.getItem(DISMISS_KEY);
      if (dismissedAt) {
        const ageDays = (Date.now() - Number(dismissedAt)) / (1000 * 60 * 60 * 24);
        if (ageDays < DISMISS_TTL_DAYS) return;
      }
    } catch (_) {}

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) {}
      celebrateInstall();
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) {}
  };

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      setVisible(false);
      setDeferred(null);
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) {}
      if (outcome === "accepted") celebrateInstall();
    } catch (_) {
      setVisible(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="md:hidden fixed left-3 right-3 z-40 bottom-[76px] rounded-2xl border border-emerald-200 bg-white shadow-lg shadow-emerald-900/10 p-3 flex items-center gap-3"
      style={{ marginBottom: "env(safe-area-inset-bottom, 0)" }}
    >
      <div className="shrink-0 h-10 w-10 rounded-xl bg-emerald-600 text-white grid place-items-center">
        <Download size={20} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900 leading-tight">
          Install PM Poshan as an app
        </p>
        <p className="text-[11px] text-slate-500 leading-tight mt-0.5">
          One-tap access from your home screen. Works offline-friendly.
        </p>
      </div>
      <button
        onClick={install}
        data-testid="pwa-install-btn"
        className="shrink-0 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-2"
      >
        Install
      </button>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="pwa-install-dismiss"
        className="shrink-0 text-slate-400 hover:text-slate-700"
      >
        <X size={18} />
      </button>
    </div>
  );
}
